import React from "react";
import {
  TransactionListPeriodReportData,
  TransactionRow,
} from "@/api/fetchQuickBooksTransactionListPeriodReport/types";

interface TransactionListTableProps {
  report: TransactionListPeriodReportData | null;
}

// Created once and reused so large reports don't build a formatter per row.
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatCurrency = (amount: number): string =>
  currencyFormatter.format(amount);

// The source date is a timezone-less string (e.g. "2026-05-01 00:00:00") or an
// ISO timestamp (e.g. "2026-05-01T00:00:00Z"); show the date portion only,
// without parsing (which would risk a TZ shift).
const formatDate = (date: string): string => date.split(/[ T]/)[0];

// Memos arrive with literal "\r\n" / "\r" / "\n" sequences (backslash +
// letter), not real newlines; convert each into a single line break so the
// memo renders on separate lines without stray "\r" artifacts.
const normalizeMemo = (memo: string): string =>
  memo.replace(/\\r\\n|\\r|\\n/g, "\n");

const headerClass =
  "py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider";
const headerClassRight =
  "py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider";
const cellClass = "py-3 px-4 text-gray-900 dark:text-slate-100";
const cellClassRight = "py-3 px-4 text-right text-gray-900 dark:text-slate-100";

export const TransactionListTable: React.FC<TransactionListTableProps> = ({
  report,
}) => {
  if (!report) {
    return null;
  }

  if (report.transactions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No transactions found for the selected period.
      </div>
    );
  }

  const count = report.transactions.length;

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {count} {count === 1 ? "transaction" : "transactions"}
      </p>
      <div className="rounded-md border border-gray-200 dark:border-slate-700 overflow-x-auto bg-white dark:bg-slate-800">
        <table className="min-w-full text-sm text-gray-900 dark:text-slate-100">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
              <th className={headerClass} scope="col">
                Date
              </th>
              <th className={headerClass} scope="col">
                Transaction Type
              </th>
              <th className={headerClass} scope="col">
                Num
              </th>
              <th className={headerClass} scope="col">
                Name
              </th>
              <th className={headerClass} scope="col">
                Account
              </th>
              <th className={headerClass} scope="col">
                Account Full Name
              </th>
              <th className={headerClassRight} scope="col">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {report.transactions.map(
              (transaction: TransactionRow, index: number) => {
                const memo = normalizeMemo(transaction.memo).trim();
                return (
                  <React.Fragment
                    key={`${transaction.transactionType}-${transaction.num}-${index}`}
                  >
                    <tr
                      className={`hover:bg-gray-50 dark:hover:bg-slate-700${
                        memo
                          ? ""
                          : " border-b border-gray-200 dark:border-slate-700"
                      }`}
                    >
                      <td className={`${cellClass} whitespace-nowrap`}>
                        {formatDate(transaction.date)}
                      </td>
                      <td className={cellClass}>
                        {transaction.transactionType}
                      </td>
                      <td className={cellClass}>{transaction.num}</td>
                      <td className={cellClass}>{transaction.name}</td>
                      <td className={cellClass}>{transaction.account}</td>
                      <td className={cellClass}>
                        {transaction.accountFullName}
                      </td>
                      <td className={cellClassRight}>
                        {formatCurrency(transaction.amount)}
                      </td>
                    </tr>
                    {memo && (
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <td
                          className="px-4 pb-3 pt-0 align-top text-gray-500 dark:text-gray-400"
                          colSpan={7}
                        >
                          <span className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500">
                            Memo
                          </span>
                          <div className="mt-1 whitespace-pre-line">{memo}</div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              },
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
